import React, {useState, useEffect, useContext} from "react";
import equal from "fast-deep-equal";
import {FeatureFlagsContext} from "@core-ui/feature-flags";
import type {
  FetchStoresOptions,
  BopisDataType,
  BopisStorageType,
  ProductStore,
  LasConfigType,
  PersonalizationDataType,
  AbbrBrand,
  FetchStatusType,
} from "./types/bopis";
import {fetchStores} from "./util/fetch-stores";
import {
  useBopisStorage,
  useBopisStorageEffect,
} from "./providers/bopis-storage";
import getGeoLocation from "./util/get-geolocation";
import {getBopisData} from "./collaborators/bopis-data";
import {fetchStoresErrorHandler} from "./collaborators/fetch-store-error-handler";

const BOPIS_CATEGORY_FEATURE_KEY = "bopis-category";

type BopisPropsType = {
  brand: AbbrBrand;
  market: string;
  pageType: string;
  personalizationData: PersonalizationDataType;
  locationAttributeServiceConfig: LasConfigType;
  errorLogger: (error: string | Error) => void;
  children: React.ReactNode;
  active?: boolean;
  enabled?: boolean;
  stores?: Array<ProductStore>;
  radius?: number | null;
  size?: number | null;
  enableStorageEffect?: boolean;
};

const BopisContext = React.createContext<BopisDataType>({} as BopisDataType);

const BopisProvider = ({
  brand,
  market,
  errorLogger,
  pageType,
  active: activeProp = undefined,
  enabled: enabledProp = undefined,
  // set initial value to undefined so it follows the behavior with last version
  stores: storesProp = undefined,
  personalizationData,
  locationAttributeServiceConfig,
  radius: radiusProp = null,
  size: sizeProp = null,
  enableStorageEffect = false,
  children,
}: BopisPropsType) => {
  // api to modify local storage
  const bopisStorage = useBopisStorage();
  const {enabledFeatures: features = {}} =
    useContext<{enabledFeatures: Record<string, boolean>}>(FeatureFlagsContext);

  // initial value to check if bopis is enabled in feature level
  let bopisEnabled = false;
  if (pageType === "product") {
    bopisEnabled = features && features[`bopis-${market}-${brand}`] === true;
  }

  if (pageType === "category") {
    bopisEnabled =
      features &&
      features[BOPIS_CATEGORY_FEATURE_KEY] === true &&
      features[`bopis-${market}-${brand}`] === true;
  }

  // initial value to check if bopis curbside is enabled in feature level
  let bopisCurbsideEnabled = false;
  if (features[`bopis-curbside-${market}-${brand}`]) {
    bopisCurbsideEnabled = true;
  }

  let fields = "timeZone,hours,latitude,longitude,address,phoneNumber,distance";
  if (bopisCurbsideEnabled) {
    fields =
      "activeFeatureToggles,hours,address,phoneNumber,distance,latitude,longitude";
  }

  const lasConfig: LasConfigType = {
    fields,
    ...locationAttributeServiceConfig,
  };

  if (!lasConfig.brandCode) {
    lasConfig.brandCode = brand;
  }

  if (!lasConfig.marketCode) {
    lasConfig.marketCode = market.toUpperCase();
  }
  // constructor END

  // states
  const [bopisInitialed, setBopisInitialed] = useState<boolean>(!bopisEnabled);
  const [size, setSize] = useState<number | null>(sizeProp || null);
  const [radius, setRadius] = useState<number | null>(radiusProp || null);
  const [fetchStatus, setFetchStatus] = useState<FetchStatusType>("complete");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [data, setData] = useState<Omit<BopisStorageType, "expiration">>(() => {
    const storageData = bopisStorage.getData();
    if (storageData && !equal({}, storageData) && !bopisStorage.hasExpired()) {
      return storageData;
    }

    return {
      active: activeProp,
      enabled: typeof enabledProp !== "undefined" ? enabledProp : bopisEnabled,
      postalCode: personalizationData.zipCode || "",
      stores: storesProp,
    };
  });
  // states END

  // need to add size, radius changing
  useEffect(() => {
    const fetchData = async () => {
      const {isLoading, isEmpty} = personalizationData;
      if (!isLoading && !isEmpty && !bopisInitialed) {
        // only fetch when we have postalCode and bopis not initial yet
        if (
          (!bopisInitialed && data.postalCode !== "") ||
          bopisStorage.get("enabled")
        ) {
          const geoLocationData = getGeoLocation(personalizationData, market);
          try {
            setFetchStatus("loading");
            const bopisData = await getBopisData({
              bopisStorage,
              lasConfig,
              geoLocationData,
              options: {
                radius,
                size,
                updateStorage: false,
              },
            });
            // TODO: need test
            setBopisInitialed(true);
            setData(bopisData);
            // TODO: need test
            setFetchStatus("complete");
            // TODO: need test
            setErrorMessage("");
          } catch (err) {
            fetchStoresErrorHandler({error: err as Error, errorLogger});
            setFetchStatus("error");
            setErrorMessage((err as Error).message);
          }
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalizationData]);

  useEffect(() => {
    const {isLoading, isEmpty} = personalizationData;
    if (!isLoading && !isEmpty) {
      const storageData = bopisStorage.getData();
      const {
        active: prevActive,
        enabled: prevEnabled,
        postalCode: prevPostalCode,
        selectedStore: prevSelectedStore,
        stores: prevStores,
      } = storageData;

      const {active, enabled, postalCode, selectedStore, stores} = data || {};

      const updatedBopisData = {} as Omit<BopisStorageType, "expiration">;

      if (prevActive !== active) {
        updatedBopisData.active = active;
        updatedBopisData.activedPageType = active && pageType;
      }

      if (prevEnabled !== enabled) {
        updatedBopisData.enabled = enabled;
      }

      if (prevPostalCode !== postalCode) {
        updatedBopisData.postalCode = postalCode;
      }

      if (!equal(prevStores, stores)) {
        updatedBopisData.stores = stores;
      }

      if (!equal(prevSelectedStore, selectedStore)) {
        updatedBopisData.selectedStore = selectedStore;
      }
      bopisStorage.update(updatedBopisData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, personalizationData]);

  useBopisStorageEffect((oldValue, newValue) => {
    // TODO: useBopisStorageEffect is still an exprimental feature for this package,
    // a full test is needed before we enable this feature.
    if (newValue && enableStorageEffect) {
      if (!data) {
        setData(() => ({
          ...newValue,
        }));
      } else if (
        data.postalCode !== newValue.postalCode ||
        !equal(data.stores, newValue.stores) ||
        !equal(data.selectedStore, newValue.selectedStore)
      ) {
        const updatedStoreData = {} as BopisStorageType;
        if (newValue.postalCode && data.postalCode !== newValue.postalCode) {
          updatedStoreData.postalCode = newValue.postalCode;
        }
        if (newValue.stores && !equal(data.stores, newValue.stores)) {
          updatedStoreData.stores = newValue.stores;
        }
        if (
          newValue.selectedStore &&
          !equal(data.selectedStore, newValue.selectedStore)
        ) {
          updatedStoreData.selectedStore = newValue.selectedStore;
        }
        setData(() => ({
          ...data,
          ...updatedStoreData,
        }));
      }
    }
  });

  // actions
  // note: this action won't change anything in localStorage,
  // consumer need to update state and trigger the side effect manually
  const getStores = async (
    zipCode: string,
    options: FetchStoresOptions = {}
  ): Promise<Omit<BopisStorageType, "expiration">> => {
    const {isBopis = true} = options;
    const application = isBopis ? "BOPIS" : "";
    const config = {
      ...lasConfig,
      application,
    };

    let bopisData = {
      ...data,
      postalCode: zipCode,
    } as Omit<BopisStorageType, "expiration">;

    if (
      zipCode !== data.postalCode &&
      options.radius !== radius &&
      options.size !== size
    ) {
      try {
        setFetchStatus("loading");
        bopisData = (await fetchStores(
          bopisStorage,
          zipCode,
          "", // latitude
          "", // longitude
          {
            updateStorage: false,
            ...options,
          },
          config,
          true
        )) as BopisDataType;
        setFetchStatus("complete");
        setErrorMessage("");

        // only record size/radius to make sure we dont fetch with same params continuously
        // consumer will not use this since they will have these value from their side
        setSize(size);
        setRadius(radius);
      } catch (err) {
        fetchStoresErrorHandler({error: err as Error, errorLogger});
        setFetchStatus("error");
        setErrorMessage((err as Error).message);
      }
    }

    return bopisData;
  };

  const resetFetchStatus = (): void => {
    setFetchStatus("complete");
  };

  const setActive = (active: boolean): void => {
    setData((prevData) => ({
      ...prevData,
      active,
    }));
  };

  const setEnabled = (enabled: boolean): void => {
    setData((prevData) => ({
      ...prevData,
      enabled,
    }));
  };

  const setPostalCode = (postalCode: string): void => {
    setData((prevData) => ({
      ...prevData,
      postalCode,
    }));
  };

  const setSelectedStore = (selectedStore: ProductStore): void => {
    setData((prevData) => ({
      ...prevData,
      selectedStore,
    }));
  };

  const setStores = (stores: ProductStore[]): void => {
    setData((prevData) => ({
      ...prevData,
      stores,
    }));
  };
  // actions END

  const providerValue = {
    ...data,
    bopisInitialed,
    setActive,
    setBopisData: setData,
    setEnabled,
    setSelectedStore,
    setStores,
    setPostalCode,
    getStores,
    fetchStatus,
    resetFetchStatus,
    errorMessage,
  } as BopisDataType;

  return (
    <BopisContext.Provider value={providerValue}>
      {children}
    </BopisContext.Provider>
  );
};

export {BopisProvider, BopisContext};
